
import { Component, Prop, Vue } from 'vue-property-decorator';
import Project from '@/models/Project';
import ProjectShellComponent from '@/components/views/NewProjectView/ProjectShellComponent.vue';
import PaymentRecordsTableNew from '@/components/views/NewProjectView/Payments/PaymentRecordsTableNew.vue';
import PaymentRecordHandler from '@/components/views/NewProjectView/Payments/PaymentRecordHandler.vue';
import PaymentOfferSelector from '@/components/views/NewProjectView/Payments/PaymentOfferSelector.vue';
import PaymentOfferSendButton from '@/components/views/NewProjectView/Payments/PaymentOfferSendButton.vue';
import PaymentOfferDeleteButton from '@/components/views/NewProjectView/Payments/PaymentOfferDeleteButton.vue';
import { ProjectTabValues } from '@/enums/components/Project/ProjectTabValues';
import { ProjectStates } from '@/enums/components/Project/ProjectStates';
import Offer from '@/models/Offer';
import PaymentRecord from '@/models/PaymentRecord';

@Component({
    name: 'ProjectTabPayments',
    components: {
        PaymentRecordHandler,
        ProjectShellComponent,
        PaymentRecordsTableNew,
        PaymentOfferSelector,
        PaymentOfferSendButton,
        PaymentOfferDeleteButton,
    },
})
export default class ProjectTabPayments extends Vue {
    @Prop({ required: true }) private projectId!: string;
    @Prop({ required: true }) private project!: Project;
    private editingPaymentRecordId: null | string = null;
    @Prop({ default: [] }) private loadedKeys!: Set<ProjectTabValues>;
    @Prop({ required: true }) private lockEditing!: boolean;
    @Prop({ required: true }) private selectedOffer!: any;

    private selectedOfferId: string = '';
    private selectedPayment: string = '';

    private get payments() {
        return this.project.paymentRecords;
    }

    private get isOrderState() {
        return this.project.state === ProjectStates.ORDER;
    }

    private get isAnyPaymentSelected() {
        if (this.payments == null || this.payments.length === 0) {
            return false;
        }

        return !!this.selectedPayment;
    }

    private handleEditing(paymentRecordId: string) {
        this.editingPaymentRecordId = paymentRecordId;
    }

    private confirmDeletingPayment() {
        if (this.selectedPayment === '') {
            return;
        }

        this.$confirm({
            title: this.$t('Are you sure you want to delete the payment?'),
            content: this.$t('This action cannot be undone.'),
            okText: this.$t('Delete') as string,
            cancelText: this.$t('Cancel') as string,
            onOk: () => {
                this.deletePaymentData();
            },
        });
    }

    private async deletePaymentData() {
        this.$emit('update:isTabLoading', true);

        try {
            await PaymentRecord.deleteExisting(this.selectedPayment);
        } catch (e) {
            this.$emit('update:isLoading', false);
            return;
        }

        this.selectedPayment = '';
        this.$emit('update:isTabLoading', false);
    }

    private confirmSendingPayment() {
        if (this.selectedPayment === '') {
            return;
        }

        this.$confirm({
            title: this.$t('Are you sure you want to send money to BC, and send the letter to the client?'),
            content: this.$t('This action cannot be undone.'),
            okText: this.$t('Send') as string,
            cancelText: this.$t('Cancel') as string,
            onOk: () => {
                this.sendPaymentData();
            },
        });
    }

    private async sendPaymentData() {
        this.$emit('update:isTabLoading', true);

        try {
            await PaymentRecord.updateExistingPaymentStatus(this.selectedPayment, { toBeSent: true });
        } catch (e) {
            this.$emit('update:isLoading', false);
            return;
        }

        try {
            await Offer.orderAccountingOffer(this.selectedOfferId);
        } catch (e) {
            console.warn('Expected error in orderAccountingOffer:', e);
        } finally {
            this.selectedPayment = '';
            this.$emit('update:isTabLoading', false);
        }
    }

    private updateSelectedRows(selectedRow: string) {
        if (this.selectedPayment === selectedRow) {
            this.selectedPayment = '';
            return;
        }

        this.selectedPayment = selectedRow;
    }

    private async created() {
        if (this.loadedKeys.has(ProjectTabValues.Payments)) {
            return;
        }
        this.$emit('update:isTabLoading', true);
        this.$emit('update:loadedKeys', new Set([...this.loadedKeys, ProjectTabValues.Payments]));
        this.$emit('update:isTabLoading', false);
    }
}
